import React from 'react';
import { getProviderFillClass } from '../../utils/providerColors';

interface ProviderCost {
  provider: string;
  cost: number;
  percentage: number;
}

interface CostByProviderProps {
  providers: ProviderCost[];
  currency: string;
}

/**
 * Cost by Provider Component
 * Phase 3: Multi-cloud support - displays cost breakdown by cloud provider
 */
export const CostByProvider: React.FC<CostByProviderProps> = ({ providers, currency }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getProviderColor = (provider: string): string => {
    return getProviderFillClass(provider);
  };

  const getProviderLabel = (provider: string): string => {
    const labels: Record<string, string> = {
      aws: 'AWS',
      azure: 'Azure',
      gcp: 'GCP',
    };
    return labels[provider.toLowerCase()] || provider;
  };

  // Sort by cost descending
  const sortedProviders = [...providers].sort((a, b) => b.cost - a.cost);

  return (
    <div className="rounded-lg border border-border bg-card p-6 dark:border-border dark:bg-card">
      <h3 className="mb-4 text-lg font-semibold text-foreground">
        Cost by Provider
      </h3>

      {sortedProviders.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No provider data available
        </div>
      ) : (
        <div className="space-y-3">
          {sortedProviders.map((item) => (
            <div key={item.provider} className="flex items-center gap-3">
              {/* Provider name and cost */}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">
                    {getProviderLabel(item.provider)}
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {formatCurrency(item.cost)}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div
                    className={`h-full ${getProviderColor(item.provider)} transition-all duration-300`}
                    style={{ width: `${item.percentage}%` }}
                    role="progressbar"
                    aria-valuenow={item.percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${getProviderLabel(item.provider)} cost percentage`}
                  />
                </div>

                {/* Percentage */}
                <div className="mt-1 text-xs text-muted-foreground">
                  {item.percentage.toFixed(1)}% of total
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
